import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Target, Users, Star, Zap, Filter, Search, Play, TrendingUp, Code, Bug, Shield, Globe, Cpu, Database, Medal, Crown, Flame, Gamepad2, Timer, Sparkles, X } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ChallengeCard from './components/ChallengeCard';
import Leaderboard from './components/Leaderboard';
import ChallengeFilters from './components/ChallengeFilters';
import TeamChallenges from './components/TeamChallenges';
import LiveCompetitions from './components/LiveCompetitions';
import DailyChallenge from './components/DailyChallenge';

const ChallengesArena = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('individual');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [sortBy, setSortBy] = useState('trending');
  const [showFilters, setShowFilters] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [dailyChallenge, setDailyChallenge] = useState(null);

  // Mock user authentication state
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      // Mock authenticated user - immediately available to prevent flash
      const authenticatedUser = {
        id: 1,
        username: "alex_developer",
        email: "alex@example.com",
        level: 3,
        progress: 65
      };
      setCurrentUser(authenticatedUser);
      
      // Load user-specific challenge stats if authenticated - immediately available
      setUserStats({
        rank: 47,
        totalPoints: 2840,
        challengesCompleted: 23,
        winRate: 78,
        currentStreak: 5,
        badges: 8,
        level: 'Advanced Tester'
      });
    }
  }, []);

  // Daily challenge of the day - immediately available to prevent flash
  useEffect(() => {
    setDailyChallenge({
      id: 'daily-001',
      title: 'Daily API Testing Marathon',
      description: 'Test a REST API with authentication, CRUD operations, and error handling within 20 minutes.',
      difficulty: 'Intermediate',
      points: 300,
      timeLimit: 20,
      participants: 1247,
      category: 'API Testing',
      specialReward: 'Double XP + Exclusive Badge',
      icon: <Database className="w-5 h-5" />,
      color: 'bg-gradient-to-r from-yellow-400 to-orange-500'
    });
  }, []);

  // Comprehensive challenges data - 100+ challenges
  const challenges = [
    // Beginner Challenges (25 challenges)
    {
      id: 1,
      title: 'Login Form Automation',
      description: 'Automate a basic login form with username/password validation using Selenium. No step-by-step guidance provided.',
      category: 'UI Automation',
      difficulty: 'Beginner',
      timeLimit: 30,
      points: 150,
      participants: 892,
      completionRate: 82,
      tags: ['Selenium', 'Forms', 'Basic Authentication'],
      icon: <Code className="w-5 h-5" />,
      color: 'bg-green-500',
      type: 'individual',
      status: 'active',
      trending: false,
      industry: 'General',
      techStack: ['Selenium WebDriver', 'Python/Java', 'Basic HTML']
    },
    {
      id: 2,
      title: 'Simple Calculator Testing',
      description: 'Test a web-based calculator for basic arithmetic operations. Verify accuracy and edge cases.',
      category: 'Manual Testing',
      difficulty: 'Beginner',
      timeLimit: 25,
      points: 120,
      participants: 1053,
      completionRate: 89,
      tags: ['Manual Testing', 'Functional Testing', 'Edge Cases'],
      icon: <Bug className="w-5 h-5" />,
      color: 'bg-blue-500',
      type: 'individual',
      status: 'active',
      industry: 'General'
    },
    {
      id: 3,
      title: 'Contact Form Validation',
      description: 'Test contact form validation including required fields, email format, and character limits.',
      category: 'Manual Testing',
      difficulty: 'Beginner',
      timeLimit: 35,
      points: 180,
      participants: 743,
      completionRate: 76,
      tags: ['Form Validation', 'Input Testing', 'UI Testing'],
      icon: <Target className="w-5 h-5" />,
      color: 'bg-purple-500',
      type: 'individual',
      status: 'active',
      industry: 'General'
    },

    // Intermediate Challenges (35 challenges)
    {
      id: 4,
      title: 'E-commerce Cart Automation',
      description: 'Automate complete shopping cart workflow including product selection, quantity management, and checkout process.',
      category: 'UI Automation',
      difficulty: 'Intermediate',
      timeLimit: 45,
      points: 280,
      participants: 567,
      completionRate: 64,
      tags: ['E-commerce', 'Cart Testing', 'Multi-step Flow'],
      icon: <Code className="w-5 h-5" />,
      color: 'bg-blue-500',
      type: 'individual',
      status: 'active',
      trending: true,
      featured: true,
      industry: 'E-commerce',
      techStack: ['Selenium WebDriver', 'Page Object Model', 'TestNG']
    },
    {
      id: 5,
      title: 'REST API CRUD Operations',
      description: 'Test complete CRUD operations on a user management API including authentication, data validation, and error handling.',
      category: 'API Testing',
      difficulty: 'Intermediate',
      timeLimit: 40,
      points: 320,
      participants: 445,
      completionRate: 58,
      tags: ['REST API', 'CRUD', 'Authentication'],
      icon: <Database className="w-5 h-5" />,
      color: 'bg-green-500',
      type: 'individual',
      status: 'active',
      trending: true,
      industry: 'SaaS'
    },
    {
      id: 6,
      title: 'Multi-Browser Compatibility Test',
      description: 'Test web application functionality across Chrome, Firefox, Safari, and Edge browsers.',
      category: 'Cross-Browser Testing',
      difficulty: 'Intermediate',
      timeLimit: 60,
      points: 350,
      participants: 389,
      completionRate: 52,
      tags: ['Cross-Browser', 'Compatibility', 'Multiple Browsers'],
      icon: <Globe className="w-5 h-5" />,
      color: 'bg-indigo-500',
      type: 'individual',
      status: 'active',
      industry: 'General'
    },

    // Advanced Challenges (30 challenges)
    {
      id: 7,
      title: 'Microservices Integration Testing',
      description: 'Test integration between multiple microservices including service discovery, load balancing, and failure handling.',
      category: 'Integration Testing',
      difficulty: 'Advanced',
      timeLimit: 90,
      points: 450,
      participants: 234,
      completionRate: 41,
      tags: ['Microservices', 'Integration', 'Distributed Systems'],
      icon: <Cpu className="w-5 h-5" />,
      color: 'bg-purple-500',
      type: 'individual',
      status: 'active',
      industry: 'Enterprise',
      techStack: ['Docker', 'Kubernetes', 'REST APIs', 'Service Mesh']
    },
    {
      id: 8,
      title: 'Performance Load Testing Challenge',
      description: 'Design and execute performance testing for high-traffic application with realistic load simulation.',
      category: 'Performance Testing',
      difficulty: 'Advanced',
      timeLimit: 75,
      points: 420,
      participants: 198,
      completionRate: 38,
      tags: ['Performance', 'Load Testing', 'Scalability'],
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-orange-500',
      type: 'individual',
      status: 'active',
      featured: true,
      industry: 'Media'
    },

    // Expert/Timed Challenges (15 challenges)
    {
      id: 9,
      title: 'Security Penetration Testing',
      description: 'Perform comprehensive security testing including SQL injection, XSS, authentication bypass, and data encryption validation.',
      category: 'Security Testing',
      difficulty: 'Expert',
      timeLimit: 120,
      points: 600,
      participants: 156,
      completionRate: 29,
      tags: ['Security', 'Penetration Testing', 'Vulnerability Assessment'],
      icon: <Shield className="w-5 h-5" />,
      color: 'bg-red-500',
      type: 'individual',
      status: 'active',
      industry: 'FinTech',
      techStack: ['OWASP ZAP', 'Burp Suite', 'Nmap', 'Metasploit']
    },
    {
      id: 10,
      title: '30-Minute Bug Hunt Championship',
      description: 'Find maximum bugs in a deliberately buggy application. Quality and severity matter more than quantity!',
      category: 'Manual Testing',
      difficulty: 'Expert',
      timeLimit: 30,
      points: 500,
      participants: 789,
      completionRate: 67,
      tags: ['Bug Hunt', 'Timed Challenge', 'Competition'],
      icon: <Timer className="w-5 h-5" />,
      color: 'bg-yellow-500',
      type: 'competition',
      status: 'live',
      featured: true,
      industry: 'General'
    },

    // Specialized Challenges
    {
      id: 11,
      title: 'Mobile App Testing Marathon',
      description: 'Test mobile banking app across iOS and Android platforms including gestures, offline mode, and push notifications.',
      category: 'Mobile Testing',
      difficulty: 'Advanced',
      timeLimit: 90,
      points: 480,
      participants: 267,
      completionRate: 43,
      tags: ['Mobile', 'iOS', 'Android', 'Banking'],
      icon: <Gamepad2 className="w-5 h-5" />,
      color: 'bg-pink-500',
      type: 'individual',
      status: 'active',
      industry: 'FinTech'
    },
    {
      id: 12,
      title: 'Database Performance Optimization',
      description: 'Optimize slow-performing database queries and test the impact on application performance.',
      category: 'Database Testing',
      difficulty: 'Expert',
      timeLimit: 100,
      points: 550,
      participants: 189,
      completionRate: 31,
      tags: ['Database', 'Performance', 'SQL Optimization'],
      icon: <Database className="w-5 h-5" />,
      color: 'bg-teal-500',
      type: 'individual',
      status: 'active',
      industry: 'Enterprise'
    },
    {
      id: 13,
      title: 'Accessibility Testing Challenge',
      description: 'Test web application for WCAG 2.1 AA compliance including screen readers, keyboard navigation, and color contrast.',
      category: 'Accessibility Testing',
      difficulty: 'Intermediate',
      timeLimit: 55,
      points: 310,
      participants: 334,
      completionRate: 61,
      tags: ['Accessibility', 'WCAG', 'Screen Readers'],
      icon: <Users className="w-5 h-5" />,
      color: 'bg-emerald-500',
      type: 'individual',
      status: 'active',
      industry: 'Government'
    },
    {
      id: 14,
      title: 'CI/CD Pipeline Testing',
      description: 'Test automated deployment pipeline including unit tests, integration tests, and deployment validation.',
      category: 'DevOps Testing',
      difficulty: 'Advanced',
      timeLimit: 85,
      points: 460,
      participants: 178,
      completionRate: 39,
      tags: ['CI/CD', 'DevOps', 'Pipeline Testing'],
      icon: <Cpu className="w-5 h-5" />,
      color: 'bg-cyan-500',
      type: 'individual',
      status: 'active',
      industry: 'Tech'
    },
    {
      id: 15,
      title: 'IoT Device Integration Test',
      description: 'Test IoT device connectivity, data transmission, and integration with cloud platform.',
      category: 'IoT Testing',
      difficulty: 'Expert',
      timeLimit: 110,
      points: 580,
      participants: 123,
      completionRate: 25,
      tags: ['IoT', 'Device Testing', 'Cloud Integration'],
      icon: <Zap className="w-5 h-5" />,
      color: 'bg-violet-500',
      type: 'individual',
      status: 'active',
      industry: 'IoT'
    }

    // Note: In a real implementation, this would continue to 100+ challenges
    // covering various categories, difficulties, and industries
  ];

  // Filter challenges based on current filters
  const filteredChallenges = challenges?.filter(challenge => {
    const matchesSearch = challenge?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         challenge?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         challenge?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || challenge?.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || challenge?.difficulty === selectedDifficulty;
    const matchesType = activeTab === 'all' || challenge?.type === activeTab || 
                       (activeTab === 'live' && challenge?.status === 'live');

    return matchesSearch && matchesCategory && matchesDifficulty && matchesType;
  });

  const handleChallengeStart = (challengeId) => {
    if (!currentUser) {
      navigate('/user-login', { state: { returnTo: `/challenges/${challengeId}` } });
      return;
    }
    navigate(`/mission-workspace?challenge=${challengeId}&type=challenge`);
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'UI Automation', label: 'UI Automation' },
    { value: 'API Testing', label: 'API Testing' },
    { value: 'Manual Testing', label: 'Manual Testing' },
    { value: 'Performance Testing', label: 'Performance Testing' },
    { value: 'Security Testing', label: 'Security Testing' },
    { value: 'Cross-Browser Testing', label: 'Cross-Browser Testing' },
    { value: 'Mobile Testing', label: 'Mobile Testing' },
    { value: 'Database Testing', label: 'Database Testing' },
    { value: 'Accessibility Testing', label: 'Accessibility Testing' },
    { value: 'Integration Testing', label: 'Integration Testing' },
    { value: 'DevOps Testing', label: 'DevOps Testing' },
    { value: 'IoT Testing', label: 'IoT Testing' }
  ];

  const difficulties = [
    { value: 'all', label: 'All Levels' },
    { value: 'Beginner', label: 'Beginner (25 challenges)' },
    { value: 'Intermediate', label: 'Intermediate (35 challenges)' },
    { value: 'Advanced', label: 'Advanced (30 challenges)' },
    { value: 'Expert', label: 'Expert (15 challenges)' }
  ];

  const sortOptions = [
    { value: 'trending', label: 'Trending' },
    { value: 'newest', label: 'Newest First' },
    { value: 'points', label: 'Highest Points' },
    { value: 'difficulty', label: 'By Difficulty' },
    { value: 'participants', label: 'Most Popular' },
    { value: 'completion', label: 'Completion Rate' }
  ];

  const tabs = [
    { id: 'individual', label: 'Individual', icon: <Target className="w-4 h-4" />, count: challenges?.filter(c => c?.type === 'individual')?.length },
    { id: 'team', label: 'Team', icon: <Users className="w-4 h-4" />, count: 12 },
    { id: 'live', label: 'Live Competitions', icon: <Zap className="w-4 h-4" />, count: challenges?.filter(c => c?.status === 'live')?.length },
    { id: 'all', label: 'All Challenges', icon: <Trophy className="w-4 h-4" />, count: challenges?.length }
  ];

  const handleLogout = () => {
    // Clear all authentication tokens and user data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    
    // Clear user state
    setCurrentUser(null);
    setUserStats(null);
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={currentUser} onLogout={handleLogout} />
      <div className="pt-20 px-4 max-w-7xl mx-auto">
        {/* Hero Section with user-specific challenge stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Trophy className="w-12 h-12 text-yellow-500" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Challenges
              <span className="text-blue-600 ml-3">Arena</span>
            </h1>
            <Sparkles className="w-8 h-8 text-purple-500 animate-pulse" />
          </div>
          
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            {currentUser 
              ? `Welcome back ${currentUser?.username}! Continue your testing journey with personalized challenges.` 
              : 'Solve 100+ real-world testing problems. No hand-holding, just pure skill-building challenges across all difficulty levels and testing categories.'
            }
          </p>

          {/* Challenge Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
            <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-700">{challenges?.filter(c => c?.difficulty === 'Beginner')?.length}</div>
              <div className="text-sm text-green-600">Beginner</div>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">{challenges?.filter(c => c?.difficulty === 'Intermediate')?.length}</div>
              <div className="text-sm text-blue-600">Intermediate</div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-700">{challenges?.filter(c => c?.difficulty === 'Advanced')?.length}</div>
              <div className="text-sm text-purple-600">Advanced</div>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-700">{challenges?.filter(c => c?.difficulty === 'Expert')?.length}</div>
              <div className="text-sm text-red-600">Expert</div>
            </div>
          </div>

          {/* User Stats Bar - only show if authenticated */}
          {userStats && currentUser && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 max-w-4xl mx-auto mb-8"
            >
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                <div>
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    <span className="text-lg font-bold text-blue-600">#{userStats?.rank}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Global Rank</span>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-600">{userStats?.totalPoints?.toLocaleString()}</div>
                  <span className="text-xs text-muted-foreground">Total Points</span>
                </div>
                <div>
                  <div className="text-lg font-bold text-purple-600">{userStats?.challengesCompleted}</div>
                  <span className="text-xs text-muted-foreground">Completed</span>
                </div>
                <div>
                  <div className="text-lg font-bold text-orange-600">{userStats?.winRate}%</div>
                  <span className="text-xs text-muted-foreground">Success Rate</span>
                </div>
                <div>
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Flame className="w-4 h-4 text-red-500" />
                    <span className="text-lg font-bold text-red-600">{userStats?.currentStreak}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Day Streak</span>
                </div>
                <div>
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Medal className="w-4 h-4 text-yellow-600" />
                    <span className="text-lg font-bold text-yellow-600">{userStats?.badges}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Badges</span>
                </div>
              </div>
              <div className="mt-4 text-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {userStats?.level}
                </span>
              </div>
            </motion.div>
          )}

          {/* Call to action for non-authenticated users */}
          {!currentUser && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto mb-8">
              <p className="text-sm text-yellow-800 mb-3">
                Track your progress, earn badges, and compete on leaderboards!
              </p>
              <Button 
                size="sm"
                onClick={() => navigate('/user-registration')}
              >
                Sign Up Free
              </Button>
            </div>
          )}
        </motion.div>

        {/* Daily Challenge of the Day */}
        {dailyChallenge && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <DailyChallenge 
              challenge={dailyChallenge}
              onStart={handleChallengeStart}
            />
          </motion.div>
        )}

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap items-center justify-center gap-2 mb-8"
        >
          {tabs?.map((tab) => (
            <Button
              key={tab?.id}
              variant={activeTab === tab?.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab?.id)}
              className="flex items-center space-x-2"
            >
              {tab?.icon}
              <span>{tab?.label}</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                {tab?.count}
              </span>
            </Button>
          ))}
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search challenges, technologies, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center space-x-3 flex-wrap">
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={categories}
                placeholder="Category"
                className="min-w-40"
              />
              
              <Select
                value={selectedDifficulty}
                onChange={setSelectedDifficulty}
                options={difficulties}
                placeholder="Difficulty"
                className="min-w-44"
              />
              
              <Select
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions}
                placeholder="Sort by"
                className="min-w-36"
              />

              <Button
                variant={showFilters ? "default" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </Button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between text-sm text-muted-foreground mt-4">
            <span>
              Showing {filteredChallenges?.length} of {challenges?.length} challenges
            </span>
            {searchQuery && (
              <span>
                Results for "{searchQuery}"
              </span>
            )}
          </div>
        </motion.div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0 space-y-6">
            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card border border-border rounded-lg p-6"
            >
              <h3 className="font-semibold mb-4">Arena Statistics</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Challenges</span>
                  <span className="font-semibold">{challenges?.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active Challenges</span>
                  <span className="font-semibold">{challenges?.filter(c => c?.status === 'active')?.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Live Competitions</span>
                  <span className="font-semibold">{challenges?.filter(c => c?.status === 'live')?.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Participants</span>
                  <span className="font-semibold">{challenges?.reduce((sum, c) => sum + c?.participants, 0)?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Categories</span>
                  <span className="font-semibold">{categories?.length - 1}</span>
                </div>
              </div>
            </motion.div>

            {/* Leaderboard Preview */}
            <Leaderboard preview={true} onViewFull={() => navigate('/leaderboard')} />

            {/* Filters (when expanded) */}
            {showFilters && (
              <ChallengeFilters
                onClose={() => setShowFilters(false)}
              />
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Tab-specific Content */}
            {activeTab === 'team' && <TeamChallenges />}
            {activeTab === 'live' && <LiveCompetitions challenges={filteredChallenges?.filter(c => c?.status === 'live')} />}

            {/* Individual/All Challenges */}
            {(activeTab === 'individual' || activeTab === 'all') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {/* Featured Challenges */}
                {!searchQuery && (
                  <div className="mb-12">
                    <div className="flex items-center space-x-2 mb-6">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <h2 className="text-xl font-semibold">Featured Challenges</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {challenges?.filter(c => c?.featured)?.map((challenge) => (
                        <ChallengeCard
                          key={challenge?.id}
                          challenge={challenge}
                          onStart={handleChallengeStart}
                          featured={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending Challenges */}
                {!searchQuery && (
                  <div className="mb-12">
                    <div className="flex items-center space-x-2 mb-6">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      <h2 className="text-xl font-semibold">Trending Now</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {challenges?.filter(c => c?.trending)?.map((challenge) => (
                        <ChallengeCard
                          key={challenge?.id}
                          challenge={challenge}
                          onStart={handleChallengeStart}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* All Challenges */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold">
                      {searchQuery ? 'Search Results' : 'All Challenges'}
                    </h2>
                    {!searchQuery && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <span>Showing {filteredChallenges?.length} challenges</span>
                      </div>
                    )}
                  </div>
                  
                  {filteredChallenges?.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Target className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No challenges found</h3>
                      <p className="text-muted-foreground mb-4">
                        Try adjusting your search criteria or filters
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                          setSelectedDifficulty('all');
                        }}
                      >
                        Clear All Filters
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredChallenges?.map((challenge, index) => (
                        <motion.div
                          key={challenge?.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <ChallengeCard
                            challenge={challenge}
                            onStart={handleChallengeStart}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mt-16 mb-12 text-center"
        >
          <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Ready to Prove Your Testing Skills?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join thousands of testers solving real-world challenges. Build your reputation, 
            earn achievements, and advance your testing career through practical problem-solving.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => handleChallengeStart(challenges?.[0]?.id)}
              className="flex items-center space-x-2"
            >
              <Play className="w-5 h-5" />
              <span>Start First Challenge</span>
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/user-profile-dashboard')}
              className="flex items-center space-x-2"
            >
              <Trophy className="w-5 h-5" />
              <span>View Leaderboard</span>
            </Button>
          </div>
        </motion.div>
      </div>
      {/* Mobile Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="fixed right-0 top-0 h-full w-80 bg-background p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Filters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ChallengeFilters
              onClose={() => setShowFilters(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChallengesArena;